import type {CSSProperties, ReactNode} from 'react';
import {Link, useParams} from 'react-router-dom';
import {
  ArrowRight,
  ArrowUpRight,
  Car,
  Clock,
  DownloadSimple,
  MapPin,
  Phone,
  ShieldCheck,
  Sparkle,
  Star,
  Tag,
  Wrench,
} from '@phosphor-icons/react';
import type {Icon} from '@phosphor-icons/react';
import {useTenantContext} from '@/app/TenantContext';
import {ServiceRow} from '@/components/booking/ServiceRow';
import {WorkGallery} from '@/components/booking/WorkGallery';
import {AssistantSheet} from '@/components/assistant/AssistantSheet';
import {DotGlow} from '@/components/ui/DotGlow';
import {Reveal} from '@/components/layout/Reveal';
import {TenantHeader} from '@/components/layout/TenantHeader';
import {TenantLogo} from '@/components/layout/TenantLogo';
import {formatMoney} from '@/lib/money';
import {compactHours} from '@/lib/hours';
import {tenantPath} from '@/lib/tenant/resolve';
import {highlightUrl, isTemplateImage} from '@/lib/demo/media';

const CARD_ICONS: Record<string, Icon> = {
  sparkle: Sparkle,
  shield: ShieldCheck,
  star: Star,
  clock: Clock,
  phone: Phone,
  pin: MapPin,
};

function SectionTitle({children}: {children: ReactNode}) {
  return <h2 className="m-0 text-[26px] font-bold leading-tight tracking-tight text-foreground">{children}</h2>;
}

export function TenantHomePage() {
  const {tenant, services, works} = useTenantContext();
  const {slug = ''} = useParams();
  const previewServices = services.slice(0, 4);
  const minPrice = Number.isFinite(tenant.minPriceMinor)
    ? formatMoney(tenant.minPriceMinor, tenant.currency, tenant.locale)
    : null;
  const templateHero = isTemplateImage(tenant.heroImageUrl);

  return (
    <>
      <TenantHeader />

      <div
        className={`page-first hero hero-frame relative mx-4 mt-4 shrink-0 aspect-[3/2] overflow-hidden rounded-[32px] lg:aspect-auto lg:h-[300px] ${templateHero ? 'tpl-hl' : ''}`}
        style={templateHero ? ({'--hl': `url(${highlightUrl(tenant.heroImageUrl as string)})`} as CSSProperties) : undefined}
      >
        <div className="hero-grad absolute inset-0" />
        <Link
          to={tenantPath(slug, 'book')}
          className="nav-pill absolute inset-x-3 bottom-3 z-10 flex items-center justify-between rounded-full px-5 py-3.5 text-foreground"
        >
          <span className="text-[16px] font-semibold">Записаться</span>
          <ArrowUpRight size={22} aria-hidden />
        </Link>
      </div>

      <div className="stagger flex flex-col gap-8 px-4 pb-8 pt-6 lg:grid lg:grid-cols-[minmax(0,1fr)_340px] lg:gap-x-6 lg:gap-y-8">
        <div className="flex items-start justify-between gap-4 lg:col-span-2">
          <div className="flex flex-col gap-3">
            <h1 className="m-0 text-[28px] font-bold leading-tight tracking-tight text-foreground lg:text-[34px]">{tenant.name}</h1>
            {tenant.tagline ? <p className="m-0 text-[16px] leading-relaxed text-muted-foreground lg:text-[17px]">{tenant.tagline}</p> : null}
          </div>
          <Link
            to={tenantPath(slug, 'book')}
            aria-label="Записаться"
            className="hidden h-12 w-12 shrink-0 items-center justify-center rounded-full border border-white/15 text-foreground lg:flex"
          >
            <ArrowUpRight size={22} aria-hidden />
          </Link>
        </div>

        <div className="grid grid-cols-2 gap-3 lg:col-span-2 lg:grid-cols-3">
          <StatChip icon={Wrench} value={String(tenant.serviceCount)} label="услуг в прайсе" />
          <StatChip icon={Car} value={String(tenant.resourceCount)} label="бокса в работе" />
          {minPrice ? (
            <div className="panel col-span-2 flex items-center justify-between rounded-3xl px-5 py-4 lg:col-span-1 lg:flex-col lg:items-start lg:gap-1 lg:px-4">
              <span className="flex items-center gap-3 text-foreground">
                <Tag size={22} weight="fill" aria-hidden />
                <span className="text-[26px] font-semibold leading-none tracking-tight lg:text-[28px]">от {minPrice}</span>
              </span>
              <span className="text-[15px] text-muted-foreground lg:text-[16px]">за услугу</span>
            </div>
          ) : null}
        </div>

        {tenant.description ? (
          <Reveal className="lg:col-span-2">
            <p className="m-0 text-[16px] leading-relaxed text-muted-foreground">{tenant.description}</p>
          </Reveal>
        ) : null}

        <div className="contents lg:col-start-2 lg:row-start-4 lg:row-span-3 lg:flex lg:flex-col lg:gap-8 lg:self-start">
        <Reveal>
          <div className="flex flex-col gap-4">
            <SectionTitle>Запись в студию</SectionTitle>
            <div className="panel panel-glow flex flex-col gap-4 rounded-3xl p-6">
              <DotGlow seed={1} />
              <div className="flex flex-col gap-2">
                <span className="text-[22px] font-bold leading-tight tracking-tight text-foreground">
                  Свежий вид для вашего авто
                </span>
                <span className="text-[16px] leading-relaxed text-muted-foreground">
                  Выберите услугу и удобное время — остальное возьмём на себя
                </span>
              </div>
              <Link
                to={tenantPath(slug, 'book')}
                className="btn-accent booking-glow flex items-center justify-center gap-3 rounded-full px-6 py-4"
              >
                <span className="text-[16px] font-semibold">Выбрать время</span>
                <ArrowUpRight size={22} aria-hidden />
              </Link>
            </div>

            <div className="panel flex flex-col gap-4 rounded-3xl p-6">
              <div className="flex items-center gap-4">
                <span className="btn-accent flex h-14 w-14 shrink-0 items-center justify-center rounded-full">
                  <Sparkle size={26} weight="fill" aria-hidden />
                </span>
                <div className="flex min-w-0 flex-col gap-1">
                  <span className="text-[21px] font-bold leading-tight tracking-tight text-foreground">Запись с ИИ</span>
                  <span className="text-[15px] leading-snug text-muted-foreground">
                    Подберёт услугу для автомобиля и покажет актуальное время
                  </span>
                </div>
              </div>
              <Link
                to={tenantPath(slug, 'ai')}
                className="flex items-center justify-center gap-3 rounded-full border border-white/15 px-6 py-4 text-foreground"
              >
                <span className="text-[16px] font-semibold">Подобрать услугу</span>
                <ArrowUpRight size={22} aria-hidden />
              </Link>
            </div>
          </div>
        </Reveal>

        {tenant.address || tenant.hoursSummary.length > 0 ? (
          <Reveal>
            <div className="panel flex flex-col gap-4 rounded-3xl p-5">
              {tenant.address ? (
                <div className="flex items-center gap-3 text-foreground">
                  <MapPin size={24} aria-hidden />
                  <span className="text-[18px] font-semibold leading-snug">{tenant.address}</span>
                </div>
              ) : null}
              {tenant.hoursSummary.length > 0 ? (
                <div className="flex items-start gap-3 text-foreground">
                  <Clock size={24} className="mt-1 shrink-0" aria-hidden />
                  <div className="flex flex-col">
                    {compactHours(tenant.hoursSummary).map((line) => (
                      <span key={line} className="text-[18px] leading-snug">
                        {line}
                      </span>
                    ))}
                    <span className="mt-1 text-[15px] text-muted-foreground">по времени студии</span>
                  </div>
                </div>
              ) : null}
              <div className="flex items-center gap-3">
                {tenant.address ? (
                  <a
                    href={tenant.mapUrl ?? `https://yandex.ru/maps/?text=${encodeURIComponent(tenant.address)}`}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center gap-2 rounded-full border border-white/15 px-5 py-3 text-foreground"
                  >
                    <span className="text-[15px] font-semibold">Маршрут</span>
                    <ArrowUpRight size={20} aria-hidden />
                  </a>
                ) : null}
                {tenant.phone ? (
                  <a
                    href={`tel:${tenant.phone}`}
                    aria-label={`Позвонить: ${tenant.phone}`}
                    className="flex h-12 w-12 items-center justify-center rounded-full border border-white/15 text-foreground"
                  >
                    <Phone size={22} aria-hidden />
                  </a>
                ) : null}
              </div>
            </div>
          </Reveal>
        ) : null}

        </div>

        <Reveal className="lg:col-start-1">
          <div className="flex flex-col gap-4">
            <div className="flex items-baseline justify-between gap-3">
              <SectionTitle>Услуги</SectionTitle>
              <Link to={tenantPath(slug, 'services')} className="flex items-center gap-2 text-[15px] text-muted-foreground">
                Все услуги
                <ArrowRight size={20} aria-hidden />
              </Link>
            </div>
            <div className="panel flex flex-col gap-1 rounded-3xl p-2">
              {previewServices.map((service) => (
                <ServiceRow key={service.id} slug={slug} service={service} locale={tenant.locale} />
              ))}
            </div>
          </div>
        </Reveal>

        {works.length > 0 ? (
          <Reveal className="lg:col-start-1">
            <div className="flex flex-col gap-4">
              <SectionTitle>Работы студии</SectionTitle>
              <WorkGallery works={works} />
            </div>
          </Reveal>
        ) : null}

        {tenant.infoCards.length > 0 ? (
          <Reveal className="lg:col-start-1">
            <div className="flex flex-col gap-3 lg:grid lg:grid-cols-2">
              {tenant.infoCards.map((card) => {
                const CardIcon = CARD_ICONS[card.icon ?? 'sparkle'] ?? Sparkle;
                return (
                  <div key={card.id} className="panel flex items-start gap-4 rounded-3xl p-5">
                    <CardIcon size={26} className="mt-0.5 shrink-0 text-foreground" aria-hidden />
                    <div className="flex flex-col gap-1">
                      <span className="text-[17px] font-semibold text-foreground">{card.title}</span>
                      <span className="text-[15px] leading-snug text-muted-foreground">{card.body}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </Reveal>
        ) : null}

        <Reveal className="lg:col-span-2">
          <div className="panel panel-glow flex flex-col gap-5 rounded-[32px] p-6 lg:p-10">
            <DotGlow seed={4} />
            <div className="flex items-center gap-4">
              <TenantLogo name={tenant.name} logoUrl={tenant.logoUrl} className="h-14 w-14 shrink-0 rounded-2xl text-2xl" />
              <div className="flex min-w-0 flex-col">
                <span className="truncate text-[17px] font-bold text-foreground">{tenant.name}</span>
                <span className="text-[15px] text-muted-foreground">Приложение студии</span>
              </div>
            </div>
            <h2 className="m-0 text-[34px] font-bold leading-[1.05] tracking-tight text-foreground">
              Студия всегда
              <br />
              под рукой.
            </h2>
            <p className="m-0 text-[17px] leading-relaxed text-muted-foreground">
              Записывайтесь за несколько касаний, без поиска ссылки и звонков.
            </p>
            <Link
              to={tenantPath(slug, 'install')}
              className="btn-accent booking-glow flex items-center justify-center gap-3 rounded-full px-6 py-4 lg:self-start lg:px-8"
            >
              <DownloadSimple size={22} aria-hidden />
              <span className="text-[16px] font-semibold">Добавить на экран</span>
              <ArrowRight size={22} aria-hidden />
            </Link>
            <span className="text-[15px] text-muted-foreground">Бесплатно. Установка через браузер.</span>
          </div>
        </Reveal>

        <div className="flex items-center justify-between border-t border-white/10 pt-5 lg:col-span-2">
          <span className="text-[15px] text-muted-foreground">{tenant.name}</span>
          {tenant.phone ? (
            <a href={`tel:${tenant.phone}`} className="flex items-center gap-2 text-[15px] text-muted-foreground">
              Связаться
              <ArrowUpRight size={18} aria-hidden />
            </a>
          ) : null}
        </div>
      </div>
      <AssistantSheet slug={slug} scope="client" fab />
    </>
  );
}

function StatChip({icon: ChipIcon, value, label}: {icon: Icon; value: string; label: string}) {
  return (
    <div className="panel flex flex-col gap-1 rounded-3xl px-4 py-4">
      <span className="flex items-center gap-3 text-foreground">
        <ChipIcon size={22} weight="fill" aria-hidden />
        <span className="text-[24px] font-semibold leading-none tracking-tight">{value}</span>
      </span>
      <span className="text-[14px] text-muted-foreground">{label}</span>
    </div>
  );
}
